/**
 * /tracks/:id — single track view.
 *
 * Same hero treatment as the main page, plus comments. Differs from /tracks:
 *   - No TBTT banner (the track itself is the page identity)
 *   - No recent-strip or vault link (those live on /tracks)
 *   - "← back to tracks" link at the bottom
 *
 * Uses SiteHeader with adminHref="/admin/tracks" — admin shortcut takes
 * admins to the tracks list, not directly to editing this track.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext.jsx';
import { usePageTitle } from '../hooks/usePageTitle.js';
import {
  getTrack,
  getTrackReactions,
  reactToTrack,
} from '../api/tracks.js';
import SiteHeader from '../components/SiteHeader.jsx';
import YouTubeEmbed from '../components/tracks/YouTubeEmbed.jsx';
import ReactionBar from '../components/tracks/ReactionBar.jsx';
import CommentsSection from '../components/tracks/CommentsSection.jsx';
import './tracks.css';


export default function TrackDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAuth = Boolean(user);

  const [track, setTrack] = useState(null);
  const [counts, setCounts] = useState({});
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tab title is dynamic — once the track loads, use its title.
  // Falls back to a generic label while loading or if the track 404s.
  usePageTitle(track ? `${track.title} · Tasty Tracks` : 'Tasty Tracks');

  const loadEverything = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const t = await getTrack(id);
      setTrack(t);

      const reacts = await getTrackReactions(t.id);
      setCounts(reacts.counts || {});
      setMine(reacts.my_reactions || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadEverything();
  }, [loadEverything, isAuth]);

  async function handleReact(emoji) {
    if (!track) return;
    const result = await reactToTrack(track.id, emoji);
    setCounts(result.counts);
    setMine((prev) =>
      result.active ? [...prev, emoji] : prev.filter((e) => e !== emoji)
    );
  }

  // ── loading / error states ────────────────────────────
  if (loading) {
    return (
      <div className="dot-grid">
        <SiteHeader adminHref="/admin/tracks" />
        <main className="tt-page">
          <div className="tt-muted">loading…</div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dot-grid">
        <SiteHeader adminHref="/admin/tracks" />
        <main className="tt-page">
          <div className="tt-error">{error}</div>
          <p className="tt-back">
            <Link to="/tracks">← back to tracks</Link>
          </p>
        </main>
      </div>
    );
  }

  if (!track) return null;

  return (
    <div className="dot-grid">
      <SiteHeader adminHref="/admin/tracks" />

      <main className="tt-page">
        <section className="tt-hero">
          <div className="tt-hero__embed">
            <YouTubeEmbed
              youtubeId={track.youtube_id}
              title={track.title}
              isEmbeddable={track.is_embeddable}
              thumbnailUrl={track.thumbnail_url}
            />
          </div>
          <div className="tt-hero__meta">
            {track.is_current && (
              <div className="tt-hero__badge">NOW PLAYING</div>
            )}
            <h2 className="tt-hero__title">{track.title}</h2>
            <p className="tt-hero__artist">
              {track.artist}
              {track.album && <span> · {track.album}</span>}
              {track.year && <span> · {track.year}</span>}
            </p>
            {track.note && (
              <blockquote className="tt-hero__note">{track.note}</blockquote>
            )}
            <ReactionBar
              counts={counts}
              myReactions={mine}
              isAuthenticated={isAuth}
              onToggle={handleReact}
            />
          </div>
        </section>

        <section className="tt-discussion">
          <CommentsSection trackId={track.id} />
        </section>

        <p className="tt-back">
          <Link to="/tracks">← back to tracks</Link>
        </p>
      </main>
    </div>
  );
}
